import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { ResourceCategory } from '@mews-commander/data-access';
import { SelectField, SelectFieldProps } from '@mews-commander/form';
import { useResourceCategoriesSuspenseQuery } from '@mews-commander/legacy-entities/src/queries/resources';
import { createOption } from '@mews-commander/ui';
import { useFieldDependency } from '@mews/framework';
import { useTargetEnterpriseIdContext } from '../context';

const renderOptions = (resourceCategories: Array<ResourceCategory>) =>
    resourceCategories.map((resourceCategory) =>
        createOption({ value: resourceCategory.id, label: resourceCategory.name })
    );

interface FormValues {
    serviceId?: string;
    resourceCategoryIds?: Array<string>;
}

interface ResourceCategoryFieldParams
    extends Pick<SelectFieldProps, 'helper' | 'label' | 'multiselect' | 'name' | 'required'> {
    serviceIdFieldName: string;
}

export const ResourceCategoryField = ({ serviceIdFieldName, ...props }: ResourceCategoryFieldParams) => {
    const targetEnterpriseId = useTargetEnterpriseIdContext();
    const fieldName = props.name as 'resourceCategoryIds';

    const { disabled, dependencyValue: serviceId } = useFieldDependency<FormValues, 'serviceId'>({
        name: fieldName,
        dependency: serviceIdFieldName as 'serviceId',
    });

    const { data: resourceCategories, isFetching } = useResourceCategoriesSuspenseQuery({
        enterpriseId: targetEnterpriseId,
        serviceId,
    });

    const { setValue } = useFormContext<FormValues>();
    useEffect(() => {
        if (!disabled) {
            const ids = resourceCategories?.map(({ id }) => id);
            if (ids) {
                setValue(fieldName, ids);
            }
        }
    }, [disabled, fieldName, resourceCategories, setValue]);

    return (
        <SelectField
            loader={isFetching}
            options={resourceCategories ? renderOptions(resourceCategories) : []}
            compact
            {...props}
            disabled={disabled || serviceId === ''}
        />
    );
};
